/**
 * User-mgmt base URL. Same as app.user-mgmt `VITE_API_BASE_URL` (this app is CRA, so set `REACT_APP_API_BASE_URL`).
 * Build-time: configure in .env or CI; default is local api.user-mgmt on 9821.
 */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:9821";

/** `products.id` for this app (see api.user-mgmt `products` / `user_licenses`; default seed RealView = 3). */
function parseVisualizationProductId(): number | null {
   const raw = process.env.REACT_APP_VISUALIZATION_PRODUCT_ID;
   if (raw == null || String(raw).trim() === '') return null;
   const n = parseInt(String(raw).trim(), 10);
   if (!Number.isFinite(n) || n < 1) return null;
   return n;
}

/** Only fields used after GET /users/me (API may return more; we ignore the rest). */
type MeResponse = {
   id: number | string;
   email: string;
   firstName?: string | null;
   lastName?: string | null;
   isActive?: boolean;
};

interface UserInfo {
   email: string;
   id: string;
   isAdmin: boolean;
   machines: unknown[];
   nickname: string | null;
   phone: string | null;
}

export interface User {
   accessToken: string;
   user: UserInfo;
}

export interface LoginRequestPayload {
   email: string;
   password: string;
}

function base64UrlDecode(input: string): string {
   const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
   const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
   return atob(padded);
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
   try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const json = base64UrlDecode(parts[1]);
      return JSON.parse(json) as Record<string, unknown>;
   } catch {
      return null;
   }
}

function buildRolesFromClaims(payload: Record<string, unknown> | null): { name: string }[] {
   const roleNames = Array.isArray(payload?.roles) ? (payload!.roles as unknown[]) : [];
   return roleNames
      .filter((r): r is string => typeof r === 'string' && r.length > 0)
      .map((name) => ({ name }));
}

function isAdminFromClaims(payload: Record<string, unknown> | null, roles: { name: string }[]): boolean {
   const adminNames = new Set(['super_admin', 'admin', 'tenant_admin']);
   if (roles.some((r) => adminNames.has(r.name))) return true;
   const keys = Array.isArray(payload?.permissions) ? (payload!.permissions as unknown[]) : [];
   return keys.some((p) => p === 'admin' || p === 'super_admin');
}

function visualizationUserFromMe(me: MeResponse, payload: Record<string, unknown> | null): UserInfo {
   const roles = buildRolesFromClaims(payload);
   const display =
      [me.firstName, me.lastName]
         .filter((x): x is string => typeof x === 'string' && x.trim() !== '')
         .join(' ')
         .trim() || null;

   return {
      email: me.email,
      id: String(me.id),
      isAdmin: isAdminFromClaims(payload, roles),
      machines: [],
      nickname: display,
      phone: null,
   };
}

function loginError(message: string): never {
   throw new Error(message);
}

const auth = {
   login: async (payload: LoginRequestPayload): Promise<User> => {
      const productId = parseVisualizationProductId();
      if (productId === null) {
         loginError(
            'Configuration: set REACT_APP_VISUALIZATION_PRODUCT_ID in .env (products.id for RealView, e.g. 3 from default seed).',
         );
      }

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({
            email: payload.email,
            password: payload.password,
            productId,
         }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
         loginError(
            (data as { message?: string; error?: string })?.message ||
               (data as { error?: string })?.error ||
               'Login failed',
         );
      }

      const accessToken = data?.accessToken as string | undefined;
      const refreshToken = data?.refreshToken as string | undefined;

      if (!accessToken || !refreshToken) {
         loginError('Login succeeded but tokens are missing in response');
      }

      const jwtPayload = decodeJwtPayload(accessToken);

      const meResp = await fetch(`${API_BASE_URL}/users/me`, {
         headers: { Authorization: `Bearer ${accessToken}` },
      });
      const me = (await meResp.json().catch(() => null)) as MeResponse | null;

      if (!meResp.ok || !me) {
         loginError('Login succeeded but failed to load user profile');
      }

      return {
         accessToken,
         user: visualizationUserFromMe(me, jwtPayload),
      };
   },
};

export default auth;
